import {
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter,
  computed,
  signal,
} from '@angular/core';
import { RippleModule } from 'primeng/ripple';
import { MenuItem, MessageService } from 'primeng/api';
import { PanelMenuModule } from 'primeng/panelmenu';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Observable, Subscription } from 'rxjs';
import { UserService } from '../../../shared/services/user.service';
import { constants, UserRoles } from '../../../shared/constants/constants';
import { ReviewerService } from '../../../shared/services/reviewer.service';
import { AuthService } from '../../../shared/services/auth.service';
import { Reviewer, Tenant } from '../../../store/reviewer/reviewer.interface';
import { StorageService } from '../../../shared/services/storage.service';
import { Store } from '@ngrx/store';
import { BadgeModule } from 'primeng/badge';
import { EmittersService } from '../../../shared/services/emitters.service';
import { FirebaseMessagingService } from '../../../shared/services/firebase-messaging.service';
import { SkillAndCredentialService } from '../../../shared/services/skill-credential.service';
import { MessagingCountHash } from '../../client/dashboard/message/message.interface';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import {
  getSessionFirmAddress,
  getSessionFirmName,
} from '../../../store/user/user.selector';
import { DocumentService } from '../../../shared/services/document-service';
import { PipesModule } from '../../../shared/pipes/pipes.module';
import { OfferService } from '../../../shared/services/offers.service';
import { HelperService } from '../../../shared/services/helper.service';
import { environment } from '../../../../environments/environment';
import { ConfigurationService } from '../../../shared/services/configuration.service';
import { ClientPortalService } from '../../../shared/services/client-portal.service';
@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [
    RippleModule,
    PanelMenuModule,
    CommonModule,
    BadgeModule,
    AvatarModule,
    AvatarGroupModule,
    PipesModule
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
  providers: [MessageService, UserService],
  encapsulation: ViewEncapsulation.None,
})
export class SidenavComponent implements OnInit, OnDestroy {
  @Input() from: string = 'dashboard';
  @Output() toggleSideNav: EventEmitter<void> = new EventEmitter<void>();
  items: MenuItem[] = [];
  additionalItems: MenuItem[] = [];
  countSubscriber: any = [];
  private subscription: Subscription = new Subscription();
  userRole: string = '';
  unreadCountInterval: any;
  firmAddress$: Observable<string>;
  firmName$: Observable<string>;
  firmLogo!: string;

  onboardingItems = [
    {
      label: 'Onboarding',
      icon: 'space-dashboard',
      role: [UserRoles.reviewer],
    },
  ];
  mailToContact: string = environment.mailToContact;
  selectedTab = 'Dashboard';
  user: any = null;
  credentials = [];
  skillsAndExperience = [];
  reviewer!: Reviewer;
  messageCount: number = 0;
  selectedTenant: any = null;
  tenantEmitterSubscription!: Subscription;
  skillsAndExperienceSubscription!: Subscription;
  credentialEmitterSubscription!: Subscription;
  isCurrentlyAssigned: boolean = true;
  isTimeKeepingEnabled: boolean = false;
  isPresumptivelyAssigned = false;
  isCurrentlyAssignedSubscription!: Subscription;
  isMobile: boolean = false;
  /** MP-5750: client-portal session; menu is Project (Started/Published/Archived) only, firm name from the session. */
  readonly isClientPortalUser = signal(false);
  readonly clientFirmName = computed(() => this.clientPortalService.firmName());
  constructor(
    private router: Router,
    private userService: UserService,
    private reviewerService: ReviewerService,
    private authService: AuthService,
    private documentService: DocumentService,
    private storageService: StorageService,
    private store: Store,
    private eventEmitterService: EmittersService,
    private firebaseMessagingService: FirebaseMessagingService,
    public skillAndCredentialService: SkillAndCredentialService,
    private offerService: OfferService,
    public helperService :HelperService,
    private configurationService: ConfigurationService,
    private clientPortalService: ClientPortalService
  ) {
    this.firmAddress$ = this.store.select(getSessionFirmAddress);
    this.firmName$ = this.store.select(getSessionFirmName);
    this.user = this.userService.getSessionUser();
    this.userRole = this.storageService.getCookieValueByKey('role');
    this.isClientPortalUser.set(this.userRole === UserRoles.client);
  }

  ngOnInit() {
    if (window.innerWidth <= 824) {
      this.isMobile = true;
    }
    if (this.isClientPortalUser()) {
      // MP-5750: no Firebase, no unread counts, no firm settings reads; `user.firm`
      // is a bare id for this role and every firm-side call answers 403.
      this.initializeClientPortalItems();
      return;
    }
    this.initializeFirebaseMessaging();
    this.reviewer = this.reviewerService.getReviewer();
    this.firmLogo =
      this.documentService.getDocumentPathById(this.user?.firm?.logo || '') || '';
    if (Object.keys(this.reviewer).length) {
      if(this.userRole === 'REVIEWER'){
        this.getPositions();
      }
    }
    if (this.from === 'dashboard') {
      this.initializeItems();
    } else {
      this.items = this.onboardingItems;
    }

    this.initializeUnreadMessageCount();

    if (UserRoles.reviewer === this.userRole) {
      this.initializeCredentialAndSkills();
      this.subscribeEvents();
      this.selectedTenant = this.storageService.getTenant();
    }
  }

  sideNavToggle(): void {
    this.toggleSideNav.emit();
  }
  async initializeUnreadMessageCount() {
    try {
      await this.firebaseMessagingService.authenticateUser();
      if (this.userRole !== constants.userRoles.reviewer) {
        this.getUnreadMessageCount();
        this.getReviewerUnreadMessageCountByFirm();
      } else {
        this.getReviewerUnreadMessageCount();
      }
    } catch (error: any) {
      if(error && error.message === constants.firebaseUnauthenticated){
        setTimeout(() => {
          this.initializeUnreadMessageCount();
        }, 2000);
      }
    }
  }

  reEvaluateReviewerUnreadCount() {
    this.countSubscriber.forEach((o: any) => {
      o.unsubscribe();
    });

    this.getReviewerUnreadMessageCount();
  }

  subscribeEvents() {
    this.tenantEmitterSubscription =
      this.eventEmitterService.tenantEmitter.subscribe(async () => {
        this.initializeCredentialAndSkills();
        this.reEvaluateReviewerUnreadCount();
        this.selectedTenant = this.storageService.getTenant();
      });

    this.skillsAndExperienceSubscription =
      this.eventEmitterService.skillAndExperienceEmitter.subscribe(() => {
        this.getSkillAndExperience();
      });

    this.credentialEmitterSubscription =
      this.eventEmitterService.credentialEmitter.subscribe(() => {
        this.getCredential();
      });

    this.isCurrentlyAssignedSubscription =
      this.eventEmitterService.isCurrentlyAssignedEmitter.subscribe(() => { 
        if(this.userRole === 'REVIEWER'){
          this.getPositions();
        }
      });
  }

  getPositions(): void {
    this.reviewerService.getPositions().subscribe((res) => {
      this.isTimeKeepingEnabled = this.user && this.user.firm && this.user.firm.payroll.enabled && res.occupied.length && res.occupied.length == 1 && res.occupied[0].tsheets.isEnabled;
      this.isCurrentlyAssigned = res.occupied.length > 0 ? true : false;
    });
  }

  initializeCredentialAndSkills() {
    if (this.from === 'dashboard') {
      this.initializeItems();
    }

    this.getSkillAndExperience();
    this.getCredential();
  }

  getSkillAndExperience(): void {
    this.reviewerService
      .getFilteredCriteriaBasedUserSkills(this.reviewer._id)
      .subscribe((skillsAndExperience) => {
        this.skillsAndExperience = skillsAndExperience;
        if (this.from === 'dashboard') {
          this.initializeItems();
        }
        this.skillAndCredentialService.setSkills(true);
        if (!this.skillsAndExperience || !this.skillsAndExperience.length) {
          this.skillAndCredentialService.setSkills(false);
          this.navigateToGeneralIfCurrentPageDataNotFound(
            'reviewer/profile/general',
            '/reviewer/profile/skills'
          );
        }
      });
  }

  getCredential(): void {
    this.reviewerService
      .getFilteredCredentialBasedUserSkills(this.reviewer._id)
      .subscribe((credential) => {
        this.credentials = credential;
        if (this.from === 'dashboard') {
          this.initializeItems();
        }
        this.skillAndCredentialService.setCredentials(true);
        if (!this.credentials || !this.credentials.length) {
          this.skillAndCredentialService.setCredentials(false);
          this.navigateToGeneralIfCurrentPageDataNotFound(
            'reviewer/profile/general',
            '/reviewer/profile/credentials'
          );
        }
      });
  }

  navigateToGeneralIfCurrentPageDataNotFound(
    navigateTo: string,
    currentRoute: string
  ) {
    if (this.router.url === currentRoute) {
      this.goto(navigateTo);
    }
  }

  goto(url: string) {
    this.router.navigateByUrl(url);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.countSubscriber.forEach((o: any) => {
      o.unsubscribe();
    });
    clearInterval(this.unreadCountInterval);
    this.tenantEmitterSubscription &&
      this.tenantEmitterSubscription.unsubscribe();
    this.skillsAndExperienceSubscription &&
      this.skillsAndExperienceSubscription.unsubscribe();
    this.credentialEmitterSubscription &&
      this.credentialEmitterSubscription.unsubscribe();
    this.isCurrentlyAssignedSubscription &&
      this.isCurrentlyAssignedSubscription.unsubscribe();
  }

  private navigateWithMobileCheck(url: string): () => void {
    return () => {
      if (this.isMobile) {
        this.sideNavToggle();
      }
      this.router.navigate([url]);
    };
  }

  /** Project menu (Started, Published, Archived) plus Worked Together (MP-5754). No additional (settings/contact) items. */
  initializeClientPortalItems(): void {
    const currentRoute = this.router.url;
    const [started, published, archived] = constants.clientPortal.projectListUrls;
    const workedTogether = constants.clientPortal.workedTogetherUrl;
    this.items = [
      {
        label: 'Project',
        icon: 'folder-open',
        expanded: true,
        items: [
          { label: 'Started', icon: '', url: started, command: this.navigateWithMobileCheck(started) },
          { label: 'Published', icon: '', url: published, command: this.navigateWithMobileCheck(published) },
          { label: 'Archived', icon: '', url: archived, command: this.navigateWithMobileCheck(archived) },
        ],
        role: [UserRoles.client],
      },
      {
        label: 'Worked Together',
        icon: 'group',
        url: workedTogether,
        command: this.navigateWithMobileCheck(workedTogether),
        role: [UserRoles.client],
      },
    ];
    this.additionalItems = [];
    this.setActiveTab(this.items, currentRoute);
  }

  initializeItems(): void {
    const currentRoute = this.router.url;

    const profileRoute = {
      label: 'Profile',
      icon: 'person',
      expanded: this.isReviewerProfileRoute(currentRoute),
      items: [
        {
          label: 'General Information',
          icon: '',
          command: this.navigateWithMobileCheck('/reviewer/profile/general'),
          url: '/reviewer/profile/general',
        },
        {
          label: 'Education',
          icon: '',
          command: this.navigateWithMobileCheck('/reviewer/profile/education'),
          url: '/reviewer/profile/education',
        },
        {
          label: 'Credentials',
          icon: '',
          command: this.navigateWithMobileCheck('/reviewer/profile/credentials'),
          url: '/reviewer/profile/credentials',
        },
        {
          label: 'Skills & Experience',
          icon: '',
          command: this.navigateWithMobileCheck('/reviewer/profile/skills'),
          url: '/reviewer/profile/skills',
        },
        {
          label: 'Resume & Certifications',
          icon: '',
          command: this.navigateWithMobileCheck('/reviewer/profile/resume'),
          url: '/reviewer/profile/resume',
        },
      ],
      role: [UserRoles.reviewer],
    };

    if (this.selectedTenant && this.selectedTenant.firm &&
      this.selectedTenant.firm.isSingleJobTypeEnabled && 
      this.selectedTenant.firm.isSingleSearchMarketEnabled) {
      profileRoute.items = profileRoute.items.filter(
        (item) => item.label !== 'General Information'
      );
    }
    if (!this.credentials || !this.credentials.length) {
      profileRoute.items = profileRoute.items.filter(
        (item) => item.label !== 'Credentials'
      );
    }
    if (!this.skillsAndExperience || !this.skillsAndExperience.length) {
      profileRoute.items = profileRoute.items.filter(
        (item) => item.label !== 'Skills & Experience'
      );
    }

    this.items = [
      {
        label: 'Dashboard',
        icon: 'space-dashboard',
        command: this.navigateWithMobileCheck('/client/overview'),
        role: [
          UserRoles.firmManager,
          UserRoles.projectOwner,
          UserRoles.projectManager,
        ],
        url: '/client/overview',
      },
      {
        label: 'Project',
        icon: 'folder-open',
        expanded: this.isProjectRoute(currentRoute),
        items: [
          {
            label: 'Started',
            icon: '',
            url: '/client/project/started',
            command: this.navigateWithMobileCheck('/client/project/started'),
          },
          {
            label: 'Published',
            icon: '',
            command: this.navigateWithMobileCheck('/client/project/published'),
            url: '/client/project/published',
          },
          {
            label: 'Unpublished',
            icon: '',
            command: this.navigateWithMobileCheck('/client/project/unpublished'),
            url: '/client/project/unpublished',
          },
          {
            label: 'Archived',
            icon: '',
            command: this.navigateWithMobileCheck('/client/project/archived'),
            url: '/client/project/archived',
          },
        ],
        role: [
          UserRoles.firmManager,
          UserRoles.projectOwner,
          UserRoles.projectManager,
        ],
      },
      {
        label: 'People',
        icon: 'group',
        expanded: this.isPeopleRoute(currentRoute),
        items: [
          {
            label: 'All',
            icon: '',
            command: this.navigateWithMobileCheck('/client/people/all'),
            url: '/client/people/all',
          },
          {
            label: 'Worked Together',
            icon: '',
            command: this.navigateWithMobileCheck('/client/people/workTogether'),
            url: '/client/people/workTogether',
          },
          {
            label: 'Restricted',
            icon: '',
            command: this.navigateWithMobileCheck('/client/people/restricted'),
            url: '/client/people/restricted',
          },
          {
            label: 'Talent Pool',
            icon: '',
            command: this.navigateWithMobileCheck('/client/people/talentPool'),
            url: '/client/people/talentPool',
          },
        ],
        role: [
          UserRoles.firmManager,
          UserRoles.projectOwner,
          UserRoles.projectManager,
        ],
      },
      {
        label: 'Messages',
        icon: 'chat',
        command: this.navigateWithMobileCheck('/client/messaging'),
        url: '/client/messaging',
        role: [
          UserRoles.firmManager,
          UserRoles.projectOwner,
          UserRoles.projectManager,
        ],
      },
      {
        label: 'Reports',
        icon: 'summarize',
        expanded: this.isReportRoute(currentRoute),
        items: this.getReportsRoute(),
        role: [
          UserRoles.firmManager,
          UserRoles.projectOwner,
          UserRoles.projectManager,
        ],
      },
      {
        label: 'Job Postings',
        icon: 'folder-open',
        expanded: this.isJobRoute(currentRoute),
        items: [
          {
            label: 'All',
            icon: '',
            url: '/client/jobs/all',
            command: this.navigateWithMobileCheck('/client/jobs/all'),
          },
          {
            label: 'Published',
            icon: '',
            command: this.navigateWithMobileCheck('/client/jobs/published'),
            url: '/client/jobs/published',
          },
          {
            label: 'Unpublished',
            icon: '',
            command: this.navigateWithMobileCheck('/client/jobs/unpublished'),
            url: '/client/jobs/unpublished',
          },
          {
            label: 'Archived',
            icon: '',
            command: this.navigateWithMobileCheck('/client/jobs/archived'),
            url: '/client/jobs/archived',
          },
        ],
        role: [
          UserRoles.firmManager,
          UserRoles.projectOwner,
          UserRoles.projectManager,
        ],
      },
      {
        label: 'Documents',
        icon: 'lab-profile',
        command: this.navigateWithMobileCheck('/client/documents'),
        url: '/client/documents',
        role: [
          UserRoles.firmManager,
          UserRoles.projectOwner,
          UserRoles.projectManager,
        ],
      },
      {
        label: 'Firm Users',
        icon: 'verified-user',
        command: this.navigateWithMobileCheck('/client/firm-users'),
        url: '/client/firm-users',
        role: [
          UserRoles.firmManager,
          UserRoles.projectOwner,
          UserRoles.projectManager,
        ],
      },
      {
        // MP-5715: client portal foundation. Backend authorizes FIRM_MANAGER and
        // PROJECT_OWNER only; hidden by getMenuItemsByRole() when the tenant's
        // clientPortal capability is off.
        label: 'Client Groups',
        icon: 'group',
        command: this.navigateWithMobileCheck('/client/client-groups'),
        url: '/client/client-groups',
        role: [
          UserRoles.firmManager,
          UserRoles.projectOwner,
        ],
      },
      {
        label: 'Dashboard',
        icon: 'dashboard',
        command: this.navigateWithMobileCheck('/reviewer/dashboard'),
        role: [
          UserRoles.reviewer
        ],
        url: '/reviewer/dashboard',
      },
      profileRoute,
      {
        label: 'Messages',
        icon: 'chat',
        command: this.navigateWithMobileCheck('/reviewer/messages'),
        role: [UserRoles.reviewer],
        url: '/reviewer/messages',
      },
      {
        label:
          this.selectedTenant &&
          this.selectedTenant.firm &&
          this.selectedTenant.firm.isInvoicingEnabled
            ? 'Invoice Generator'
            : 'Timekeeping',
        icon: 'schedule',
        command: this.navigateWithMobileCheck('/reviewer/timekeeping'),
        role: [UserRoles.reviewer],
        url: '/reviewer/timekeeping',
      },
      {
        label: 'Past Projects',
        icon: 'past-projects',
        command: this.navigateWithMobileCheck('/reviewer/past-projects'),
        role: [UserRoles.reviewer],
        url: '/reviewer/past-projects',
      },
      {
        label: 'Job Postings',
        icon: 'summarize',
        expanded: this.isReviewerJobRoute(currentRoute),
        items: [
          {
            label: 'Active',
            icon: '',
            command: this.navigateWithMobileCheck('/reviewer/job/active'),
            url: '/reviewer/job/active',
          },
          {
            label: 'Applied',
            icon: '',
            command: this.navigateWithMobileCheck('/reviewer/job/applied'),
            url: '/reviewer/job/applied',
          }
        ],
        role: [UserRoles.reviewer],
      },
      {
        label: 'Settings',
        icon: 'settings',
        command: this.navigateWithMobileCheck('/reviewer/settings'),
        url: '/reviewer/settings',
        role: [UserRoles.reviewer],
      },
    ];

    if (
          this.selectedTenant &&
          this.selectedTenant.firm &&
          !this.selectedTenant.firm.isJobBoardEnabled
    ) {
      this.items = this.items.filter((item) => item.label !== 'Job Postings');
    }

    this.additionalItems = [
      {
        label: 'Settings',
        icon: 'settings',
        expanded: this.isSettingsRoute(currentRoute),
        items: [
          {
            label: 'Skills and Experience',
            icon: '',
            command: this.navigateWithMobileCheck(
              '/client/settings/dynamic-criteria'
            ),
            url: '/client/settings/dynamic-criteria',
          },
          {
            label: 'Credentialing',
            icon: '',
            command: this.navigateWithMobileCheck(
              '/client/settings/dynamic-credential'
            ),
            url: '/client/settings/dynamic-credential',
          },
          {
            label: 'Change Password',
            icon: '',
            command: this.navigateWithMobileCheck(
              '/client/settings/change-password'
            ),
            url: '/client/settings/change-password',
          },
        ],
        role: [
          UserRoles.firmManager,
          UserRoles.projectOwner,
          UserRoles.projectManager,
        ],
      },
      {
        label: 'Contact',
        icon: 'contact-support',
        url: `mailto:${this.mailToContact}`,
        role: [
          UserRoles.firmManager,
          UserRoles.projectOwner,
          UserRoles.projectManager,
        ],
      },
    ];

    if(!this.user.firm.isSingleSearchMarketEnabled){
      this.additionalItems[0].items?.unshift(
        {
          label: 'Search Markets',
          icon: '',
          command: () => {
            this.router.navigate(['/client/settings/search-markets']);
          },
          url: '/client/settings/search-markets',
        }
      )
    }

    if(!this.user.firm.isSingleJobTypeEnabled){
      this.additionalItems[0].items?.unshift(
        {
          label: 'Job Types',
          icon: '',
          command: () => {
            this.router.navigate(['/client/settings/job-types']);
          },
          url: '/client/settings/job-types',
        }
      )
    }

    this.setActiveTab([...this.items, ...this.additionalItems], currentRoute);
  }

  isProjectRoute(currentRoute: string): boolean {
    const projectRoutes = [
      '/client/project/started',
      '/client/project/published',
      '/client/project/unpublished',
      '/client/project/archived',
      '/client/project/create',
      '/client/project/edit',
    ];
    return projectRoutes.some((route) => currentRoute.includes(route));
  }

  isJobRoute(currentRoute: string): boolean {
    const jobRoutes = [
      '/client/jobs/all',
      '/client/jobs/published',
      '/client/jobs/unpublished',
      '/client/jobs/archived',
      '/client/jobs/create',
      '/client/jobs/edit',
    ];
    return jobRoutes.some((route) => currentRoute.includes(route));
  }

  isReviewerJobRoute(currentRoute: string): boolean {
    const reviewerJobRoutes = ['/reviewer/job/active', '/reviewer/job/applied'];
    return reviewerJobRoutes.some((route) => currentRoute.includes(route));
  }

  isPeopleRoute(currentRoute: string): boolean {
    const peopleRoutes = [
      '/client/people/all',
      '/client/people/pending',
      '/client/people/workTogether',
      '/client/people/restricted',
      '/client/people/talentPool',
      'client/people/invite-candidate',
    ];
    return peopleRoutes.some((route) => currentRoute.includes(route));
  }

  isSettingsRoute(currentRoute: string): boolean {
    const settingsRoutes = [
      '/client/settings/job-type',
      '/client/settings/search-markets',
      '/client/settings/dynamic-criteria',
      '/client/settings/dynamic-credential',
      '/client/settings/change-password',
    ];
    return settingsRoutes.some((route) => currentRoute.includes(route));
  }

  isReportRoute(currentRoute: string): boolean {
    const reportRoutes = [
      '/client/reports/payroll',
      '/client/reports/invoice',
      '/client/reports/timekeeping-summary',
      '/client/reports/all-working-reviewers',
    ];
    return reportRoutes.some((route) => currentRoute.includes(route));
  }

  isReviewerProfileRoute(currentRoute: string): boolean {
    const reviewerProfileRoutes = [
      '/reviewer/profile/general',
      '/reviewer/profile/education',
      '/reviewer/profile/credentials',
      '/reviewer/profile/skills',
      '/reviewer/profile/resume',
      '/reviewer/profile/resume-generator',
    ];
    return reviewerProfileRoutes.some((route) => currentRoute.includes(route));
  }

  setActiveTab(menuItems: Array<MenuItem>, currentRoute: string) {
    menuItems.forEach((item) => {
      if (item.items && item.items.length) {
        this.setActiveTab(item.items, currentRoute);
      } else if (item.url && currentRoute.includes(item.url)) {
        // Key the active tab off the url, not the label: labels are not unique
        // (Project and Job Postings both have Published/Unpublished/Archived,
        // People and Job Postings both have All) so a label match highlights
        // several items at once.
        this.setSelectedTab(item.url);
      }
    });
  }

  setSelectedTab(selectedTab: string) {
    this.selectedTab = selectedTab;
  }

  getReportsRoute() {
    let items = [];

    if(this.user && this.user.firm && this.user.firm.payroll && this.user.firm.payroll.enabled) {
      items.push({
        label: 'Payroll',
        icon: '',
        command: this.navigateWithMobileCheck('/client/reports/payroll'),
        url: '/client/reports/payroll',
      });
    }

    if(this.user && this.user.firm && this.user.firm.isInvoicingEnabled) {
      items.push({
        label: 'Invoice',
        icon: '',
        command: this.navigateWithMobileCheck('/client/reports/invoice'),
        url: '/client/reports/invoice',
      });
    }

    if(this.user && this.user.firm && this.user.firm.payroll && this.user.firm.payroll.enabled) {
      items.push({
        label: 'Timekeeping Summary Report',
        icon: '',
        command: this.navigateWithMobileCheck('/client/reports/timekeeping-summary'),
        url: '/client/reports/timekeeping-summary',
      });
    }

    items.push({
      label: 'Actively Working Report',
      icon: '',
      command: this.navigateWithMobileCheck(
        '/client/reports/all-working-reviewers'
      ),
      url: '/client/reports/all-working-reviewers',
    });

    return items;
  }

  getMenuItemsByRole() {
    let items = this.items;
    if (this.isClientPortalUser()) {
      return items.filter((i) => i['role'].includes(this.userRole));
    }
    if (!this.configurationService.isClientPortalEnabled()) {
      items = items.filter((item) => item.label !== 'Client Groups');
    }
    if (!this.user.firm.isDocusignEnabled) {
      items = items.filter((item) => item.label !== 'Documents');
    }
    if (!this.user.firm.isJobBoardEnabled || (this.selectedTenant && this.selectedTenant.reviewerStatus === constants.userVerificationStatus.pendingAdminVerification)) {
      items = items.filter((item) => item.label !== 'Job Postings');
    }
    if (!this.isCurrentlyAssigned) {
      items = items.filter(
        (item) =>
          item.label !== 'Invoice Generator' &&
          item.label !== 'Timekeeping'
      );
    }

    if(!this.isCurrentlyAssigned && !this.isPresumptivelyAssigned){
      items = items.filter(
        (item) =>
          item.label !== 'Messages'
      );
    }
    return items.filter((i) => {
      return i['role'].includes(this.userRole);
    });
  }

  getAdditionalMenuItemsByRole() {
    return this.additionalItems.filter((i) => {
      return i['role'].includes(this.userRole);
    });
  }

  getUnreadMessageCount() {
    this.countSubscriber.push(
      this.firebaseMessagingService
        .getFirmUnReadMessages()
        .subscribe((unReadDiscussions: any) => {
          let unreadCount = 0;
          const unreadMessageHash: MessagingCountHash = {};

          Object.values(unReadDiscussions).forEach((o: any) => {
            if (unreadMessageHash.hasOwnProperty(o.projectId)) {
              unreadMessageHash[o.projectId] += o.pmUnReadMessageCount;
            } else {
              unreadMessageHash[o.projectId] = o.pmUnReadMessageCount;
            }

            if (
              unreadMessageHash.hasOwnProperty(`${o.projectId}_${o.roleId}`)
            ) {
              unreadMessageHash[`${o.projectId}_${o.roleId}`] +=
                o.pmUnReadMessageCount;
            } else {
              unreadMessageHash[`${o.projectId}_${o.roleId}`] =
                o.pmUnReadMessageCount;
            }

            if (
              unreadMessageHash.hasOwnProperty(
                `${o.projectId}_${o.roleId}_${o.reviewerId}`
              )
            ) {
              unreadMessageHash[`${o.projectId}_${o.roleId}_${o.reviewerId}`] +=
                o.pmUnReadMessageCount;
            } else {
              unreadMessageHash[`${o.projectId}_${o.roleId}_${o.reviewerId}`] =
                o.pmUnReadMessageCount;
            }
            unreadCount += o.pmUnReadMessageCount;
          });

          this.eventEmitterService.messageCountEmitter.next(unreadMessageHash);

          this.messageCount = unreadCount;
        })
    );
  }

  getReviewerUnreadMessageCountByFirm(){
    const firmId = this.userService.getUserFirmId();
    this.firebaseMessagingService
      .getReviewerUnReadMessagesByFirm(firmId)
      .subscribe((unReadDiscussions: any) => {
        const reviewerUnreadMessageHash: MessagingCountHash = {};
        unReadDiscussions.forEach((discussion: any) => {
          if(reviewerUnreadMessageHash.hasOwnProperty(`${discussion.projectId}_${discussion.roleId}_${discussion.reviewerId}`)){
            reviewerUnreadMessageHash[`${discussion.projectId}_${discussion.roleId}_${discussion.reviewerId}`] += discussion.reviewerUnreadMessageCount;
          } else {
            reviewerUnreadMessageHash[`${discussion.projectId}_${discussion.roleId}_${discussion.reviewerId}`] = discussion.reviewerUnreadMessageCount || 0;
          }
        });
        this.eventEmitterService.reviewerMessageFromFirmCountEmitter.next(reviewerUnreadMessageHash);
      }
    )
  }

  getReviewerUnreadMessageCount() {
    this.countSubscriber.push(
      this.firebaseMessagingService
        .getReviewerUnReadMessages()
        .subscribe((unReadDiscussions: any) => {
          let unreadCount = 0;
          let Tenant = this.storageService.getTenant();
          Object.values(unReadDiscussions).forEach((o: any) => {
            if (Tenant.firm.id === o.firmId) {
              unreadCount += o.reviewerUnreadMessageCount;
            }
          });
          this.messageCount = unreadCount;
        })
    );
  }

  breakAddress(address: string, chunkSize: number = 50): string[] {
    if (!address) return [];
    // If the address is longer than 47 characters, truncate and add ellipsis
    const truncatedAddress =
      address.length > 47 ? address.slice(0, 47) + '...' : address;
    return Array.from(
      { length: Math.ceil(truncatedAddress.length / chunkSize) },
      (_, i) => truncatedAddress.slice(i * chunkSize, (i + 1) * chunkSize)
    );
  }

  private initializeFirebaseMessaging(): void {
    this.firebaseMessagingService.initFirebase();
  }
}
